import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { IconClock, IconShoppingCart, IconArrowRight, IconFilter } from "@tabler/icons-react";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";

const TRACKS = [
  { id: "todas", es: "Todos", en: "All" },
  { id: "docente", es: "Docente", en: "Teacher" },
  { id: "sombra", es: "Sombra", en: "Sombra" },
  { id: "familia", es: "Familia", en: "Family" },
  { id: "escuela", es: "Escuela", en: "School" },
];

export default function SiteProductos() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [track, setTrack] = useState("todas");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true); setError("");
      try {
        const q = track === "todas" ? "" : `?track=${track}`;
        const { data } = await api.get(`/aula/catalog${q}`);
        setItems(data);
      } catch (e) { setError(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [track]);

  const buy = (courseId) => {
    // Store intended checkout destination so post-login/register can redirect
    const target = `/app/aula/${courseId}`;
    if (user && user.id) {
      nav(target);
    } else {
      try { sessionStorage.setItem("chiessa.postLogin", target); } catch { /* noop */ }
      nav(`/register?next=${encodeURIComponent(target)}`);
    }
  };

  return (
    <div className="bg-paper min-h-screen">
      <SiteNav />
      <section className="border-b border-line">
        <div className="wrap py-14 md:py-20">
          <p className="eyebrow">{lang === "es" ? "Formación aplicada" : "Applied training"}</p>
          <h1 className="font-display font-medium text-[clamp(32px,5vw,52px)] leading-[1.05] mt-3">
            {lang === "es" ? "Productos y cursos" : "Products & courses"}
          </h1>
          <p className="text-muted text-[15px] mt-5 max-w-[42em] leading-relaxed">
            {lang === "es"
              ? "Cursos y cápsulas hechos con evidencia. Elige el que necesitás y comienza tu formación hoy mismo."
              : "Courses and capsules built with evidence. Pick what you need and start today."}
          </p>
          <div className="flex items-center gap-2 mt-8 flex-wrap" data-testid="productos-filters">
            <IconFilter size={13} className="text-tert" />
            {TRACKS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTrack(t.id)}
                className={`px-3 py-1.5 rounded-full text-[12px] font-medium transition ${
                  track === t.id
                    ? "bg-teal-deep text-white border border-teal-deep"
                    : "bg-white text-muted border border-line hover:border-teal-deep"
                }`}
                data-testid={`productos-track-${t.id}`}
              >
                {lang === "es" ? t.es : t.en}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="wrap">
          {error && <p className="text-errtext text-[13px] mb-4">{error}</p>}
          {loading ? (
            <p className="text-tert text-[12px] font-mono uppercase tracking-widest">Cargando…</p>
          ) : items.length === 0 ? (
            <div className="border border-line rounded-2xl p-12 text-center" data-testid="productos-empty">
              <p className="text-tert text-[14px]">
                {lang === "es" ? "Aún no hay productos publicados." : "No published products yet."}
              </p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="productos-grid">
              {items.map((c, idx) => {
                const isFree = c.is_free || Number(c.price || 0) === 0;
                return (
                  <motion.div
                    key={c.id}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: idx * 0.04 }}
                    className="mod-card flex flex-col"
                    data-testid={`producto-${c.id}`}
                  >
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <span className="tag">
                        {c.track === "docente" ? (lang === "es" ? "Docente" : "Teacher")
                          : c.track === "sombra" ? "Sombra"
                          : c.track === "familia" ? (lang === "es" ? "Familia" : "Family")
                          : c.track === "escuela" ? (lang === "es" ? "Escuela" : "School")
                          : (lang === "es" ? "Ambos" : "Both")}
                      </span>
                      <span className="tag tag-honey">{c.level}</span>
                      {c.is_capsule && (
                        <span className="tag" style={{ background: "#EEEDFE", color: "#3C3489" }}>
                          {lang === "es" ? "Cápsula" : "Capsule"}
                        </span>
                      )}
                    </div>
                    <h3 className="font-display text-[20px] font-medium">{c.title}</h3>
                    {c.subtitle && <p className="text-tert text-[12.5px] mt-1">{c.subtitle}</p>}
                    <p className="text-muted text-[13.5px] mt-3 leading-relaxed line-clamp-4 flex-1">
                      {c.description || (lang === "es" ? "Curso publicado por Chiessa Institute." : "Course published by Chiessa Institute.")}
                    </p>
                    <div className="flex items-center gap-3 mt-4 text-tert text-[11.5px]">
                      <span className="flex items-center gap-1"><IconClock size={12} /> {c.duration_min || "—"} min</span>
                      <span>· {c.total_lessons || 0} {lang === "es" ? "lecciones" : "lessons"}</span>
                    </div>
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-line">
                      {isFree ? (
                        <span className="tag" style={{ background: "#E7F3EE", color: "#0A5C3F" }}>
                          {lang === "es" ? "Gratuito" : "Free"}
                        </span>
                      ) : (
                        <div>
                          <p className="font-display text-[22px] font-medium text-teal-deep leading-none">${Number(c.price).toFixed(2)}</p>
                          <p className="text-tert text-[10.5px] mt-0.5">USD · {lang === "es" ? "pago único" : "one-time"}</p>
                        </div>
                      )}
                      <button
                        className="btn text-[12px]"
                        onClick={() => buy(c.id)}
                        data-testid={`producto-buy-${c.id}`}
                      >
                        {isFree ? (
                          <>{lang === "es" ? "Acceder" : "Access"} <IconArrowRight size={12} /></>
                        ) : (
                          <><IconShoppingCart size={12} /> {lang === "es" ? "Comprar" : "Buy"}</>
                        )}
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
